import React, {
  RefObject,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useIntercom } from 'react-use-intercom'
import { SideBar, SidebarLinkOption, SideBarRefType } from '@common/material'
import { PageUrls } from '@core/page-urls'
import { NotificationsList } from '@modules/notifications'
import { useGetNotificationsUnseenCounter } from '@core/react-query/features/notifications'
import { useAnalytics } from '@common/hooks'
import {
  AssignmentOutlined,
  BarChartOutlined,
  BusinessOutlined,
  FolderCopyOutlined,
  FolderOpenOutlined,
  GroupsOutlined,
  HelpOutline,
  HomeOutlined,
  Inventory2Outlined,
  MapOutlined,
  NotificationsNoneOutlined,
  PeopleAltOutlined,
  PlaceOutlined,
  ViewTimelineOutlined,
  CloudOutlined,
  CloudQueueOutlined,
  KeyOutlined,
  ArticleOutlined,
  FactCheckOutlined,
  Label,
  SettingsOutlined,
  LocalOfferOutlined,
  DashboardOutlined,
  MoreHorizOutlined,
  LockOutlined,
  VpnKeyOutlined,
  LinkOutlined,
  CodeOutlined,
  FlagOutlined,
  InsertDriveFileOutlined,
  SchoolOutlined,
  MarkEmailReadOutlined,
} from '@mui/icons-material'
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred'
import { OrganisationMenu } from './OrganisationMenu'
import { useDeepCompareMemo } from 'use-deep-compare'
import { useNavigationContext } from '@common/hooks'
import { useRouter } from 'next/router'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { useGetOrganisation } from '@core/react-query/features/organisations/organisation/hooks/useGetOrganisation'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { localizedStrings } from '@core/strings'
import { useGetUserStats } from '@core/react-query/features/profile/hooks/useGetUserStats'
import { SidebarSection } from '@common/material/Sidebar/types'
import { SidebarNewButton } from './SidebarNewButton'
import { SidebarUnimpersonateButton } from './SidebarUnimpersonateButton'
import { Drawer } from '@mui/material'
import { Breakpoint } from '@mui/material/styles'
import { useGetProject } from '@core/react-query/features/projects/project/hooks/useGetProject'
import { useGetBusinessUnit } from '@core/react-query/features/businessUnits/hooks/useGetBusinessUnit'

const NavigationAwareSidebar = forwardRef<
  SideBarRefType,
  {
    contentRef: RefObject<HTMLDivElement>
    onCollapsedChange?: (collapsed: boolean) => void
    autoCollapseBelow?: Breakpoint
  }
>(({ contentRef, onCollapsedChange, autoCollapseBelow }, ref) => {
  const router = useRouter()
  const { flags } = useFeatureFlags([
    'organisation_companies',
    'web_company_settings',
    'web_mfa_settings',
    'procore_integration',
    'web_site_box_rams_appraisals',
  ])
  const {
    organisationId,
    projectId,
    businessUnitId,
    inSupportContext,
    inProjectSettingsContext,
    inOrganisationSettingsContext,
  } = useNavigationContext()
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false)

  const { show } = useIntercom()
  const { data: organisation } = useGetOrganisation({ organisationId })
  const { data: profile } = useGetProfile()

  const { data: userStats } = useGetUserStats({
    userId: profile?.id,
    organisationId,
  })

  const { data: currentProject } = useGetProject(
    { projectId },
    {
      enabled:
        !!projectId && !inSupportContext && !inOrganisationSettingsContext,
    },
  )

  const { data: currentBusinessUnit } = useGetBusinessUnit(
    { businessUnitId },
    {
      enabled:
        !!businessUnitId &&
        !inSupportContext &&
        !inOrganisationSettingsContext &&
        !inProjectSettingsContext,
    },
  )

  const { sendAnalyticsEvent } = useAnalytics()
  const { data: unseenCounterData } = useGetNotificationsUnseenCounter(
    { userId: profile?.id },
    { enabled: !!profile },
  )

  const unseenNotificationsCount = unseenCounterData?.unseen ?? 0

  const handleNotificationClick = () => {
    if (notificationsDrawerOpen) {
      setNotificationsDrawerOpen(false)
      return
    }

    const unreadNotificationsCount = unseenCounterData?.unread ?? 0

    sendAnalyticsEvent({
      eventName: 'notification.list.opened',
      eventProperties: {
        platform: 'web',
        numberOfUnseenNotifications: unseenNotificationsCount,
        numberOfUnreadNotifications: unreadNotificationsCount,
      },
    })

    setNotificationsDrawerOpen(true)
  }

  const footerLinks = useMemo(() => {
    const links: SidebarLinkOption[] = [
      {
        title: localizedStrings.help,
        onClick: show,
        icon: <HelpOutline />,
      },
    ]
    return links
  }, [show])

  const userOrgRole = profile?.organisations?.find(
    (org) => org.id === organisationId,
  )?.role
  const currentOrgProjects = profile?.projects?.filter(
    (p) => p.organisationId === organisationId,
  )
  const isSupportRole =
    profile?.role === 'SUPPORT_USER' || profile?.role === 'SUPER_ADMIN'

  const isAccountOwner = userOrgRole === 'ACCOUNT_OWNER'

  const hasNonViewerProjectRole =
    !!currentOrgProjects?.length &&
    currentOrgProjects.some((p) => p.role !== 'PROJECT_VIEWER')

  const canSeeNewButton =
    isAccountOwner || hasNonViewerProjectRole || isSupportRole

  const isCompetencyBlocked = !!(
    organisationId && profile?.hasRequiredCompetencies(organisationId)
  )

  const organisationSidebarData = useDeepCompareMemo(() => {
    if (!organisationId || inSupportContext) return null

    if (isCompetencyBlocked) {
      const restrictedLinks: SidebarLinkOption[] = [
        {
          title: localizedStrings.notifications,
          onClick: handleNotificationClick,
          icon: <NotificationsNoneOutlined />,
          forceActive: notificationsDrawerOpen,
          badgeCount:
            unseenNotificationsCount > 0 ? unseenNotificationsCount : undefined,
          badgeVariant: 'primary',
        },
        {
          title: localizedStrings.companies,
          href: PageUrls.organisationCompanies({ organisationId }),
          icon: <BusinessOutlined />,
        },
      ]
      return {
        organisationLinks: restrictedLinks,
        moreLinks: [],
        sections: [],
        backButton: undefined,
      }
    }

    const navProjects = userStats?.projects ?? []
    const navBusinessUnits = userStats?.businessUnits ?? []
    const allProjectStats = userStats?.allProjectStats

    const organisationLinks: SidebarLinkOption[] = [
      {
        title: localizedStrings.home,
        href: PageUrls.organisation(organisationId),
        icon: <HomeOutlined />,
        exactMatch: true,
      },
      {
        title: localizedStrings.notifications,
        onClick: handleNotificationClick,
        icon: <NotificationsNoneOutlined />,
        forceActive: notificationsDrawerOpen,
        badgeCount:
          unseenNotificationsCount > 0 ? unseenNotificationsCount : undefined,
        badgeVariant: 'primary',
      },
    ]

    organisationLinks.push({
      title: localizedStrings.projects,
      href: PageUrls.projects(organisationId),
      icon: <FolderOpenOutlined />,
      dataTest: 'jsProjects',
    })

    if (profile?.isAccountOwnerOrViewer(organisationId) || isSupportRole) {
      organisationLinks.push({
        title: localizedStrings.users,
        href: PageUrls.organisationUsers(organisationId),
        icon: <PeopleAltOutlined />,
        dataTest: 'organisation-users',
      })
    }

    if (
      navProjects.length > 0 &&
      flags.organisation_companies &&
      (profile?.isCompanyAdmin(organisationId) || organisation?.canEdit())
    ) {
      organisationLinks.push({
        title: localizedStrings.companies,
        href: PageUrls.organisationCompanies({ organisationId }),
        icon: <BusinessOutlined />,
      })
    }

    const moreLinks: SidebarLinkOption[] = []

    if (navBusinessUnits.length > 0 && organisation?.canSeeBusinessUnits()) {
      moreLinks.push({
        title: localizedStrings.businessUnits,
        href: PageUrls.businessUnits(organisationId),
        icon: <GroupsOutlined />,
        dataTest: 'jsBusinessUnits',
      })
    }

    moreLinks.push({
      title: localizedStrings.analytics,
      href: PageUrls.organisationAnalytics({ organisationId }),
      icon: <BarChartOutlined />,
    })

    if (navProjects.length > 0 && organisation?.canViewTimeline()) {
      moreLinks.push({
        title: localizedStrings.timeline,
        href: PageUrls.organisationTimeline({ organisationId }),
        icon: <ViewTimelineOutlined />,
      })
    }

    const sections: SidebarSection[] = []

    const organisationSectionLinks: SidebarLinkOption[] = []

    if (allProjectStats?.hasPermits) {
      organisationSectionLinks.push({
        title: localizedStrings.permits,
        href: PageUrls.permits(organisationId),
        icon: <ArticleOutlined />,
        dataTest: 'jsPermits',
        actionTooltipLabel: localizedStrings.createPermit,
        actionHref: PageUrls.newPermit(organisationId),
      })
    }

    if (allProjectStats?.hasChecklists) {
      organisationSectionLinks.push({
        title: localizedStrings.checklists,
        href: PageUrls.checklists(organisationId),
        icon: <FactCheckOutlined />,
        dataTest: 'jsChecklists',
        actionTooltipLabel: localizedStrings.createChecklist,
        actionHref: PageUrls.newChecklist(organisationId),
      })
    }

    if (allProjectStats?.hasInspections) {
      organisationSectionLinks.push({
        title: localizedStrings.inspections,
        href: PageUrls.inspections(organisationId),
        icon: <FlagOutlined />,
        actionTooltipLabel: localizedStrings.createInspection,
        actionHref: PageUrls.newInspection(organisationId),
      })
    }

    if (allProjectStats?.hasTasks) {
      organisationSectionLinks.push({
        title: localizedStrings.tasks,
        href: PageUrls.tasks(organisationId),
        icon: <AssignmentOutlined />,
        badgeCount: userStats?.tasks?.assignedActiveTasksCount,
        actionTooltipLabel: localizedStrings.createTask,
        actionHref: PageUrls.newTask(organisationId),
      })
    }

    if (allProjectStats?.hasRegisters) {
      organisationSectionLinks.push({
        title: localizedStrings.registers,
        href: PageUrls.registers(organisationId),
        icon: <FolderCopyOutlined />,
        dataTest: 'jsRegisters',
      })
    }

    if (
      (navProjects.length > 1 || allProjectStats?.hasRegisters) &&
      organisationSectionLinks.length > 0
    ) {
      sections.push({
        title: localizedStrings.allProjects,
        links: organisationSectionLinks,
        collapsible: true,
        defaultExpanded: true,
      })
    }

    const projectsToShow = navProjects.slice(0, 5)

    if (
      projectId &&
      currentProject?.projectSidebarInfo &&
      !projectsToShow.some((p) => p.id === projectId)
    ) {
      const sidebarInfo = currentProject.projectSidebarInfo
      projectsToShow.unshift({
        id: currentProject.id,
        projectName: currentProject.name,
        hasTasks: sidebarInfo.hasTasks,
        hasPermits: sidebarInfo.hasPermits,
        hasChecklists: sidebarInfo.hasChecklists,
        hasInspections: sidebarInfo.hasInspections,
        hasRegisters: sidebarInfo.hasRegisters,
        hasPlans: sidebarInfo.hasPlans,
        hasMapConfigured: sidebarInfo.hasMapConfigured,
        hasObservations: sidebarInfo.hasObservations,
        permittedActions: currentProject.permittedActions,
        integrations: sidebarInfo.integrations,
        featureFlags: currentProject.featureFlags,
      })
    }

    if (projectsToShow.length > 0 || projectId) {
      if (projectsToShow.length === 0 && !projectId) return null

      sections.push({
        title: localizedStrings.yourProjects,
        projects: projectsToShow.map(({ projectName, ...project }) => {
          const canSeeSettingsIcon =
            isSupportRole ||
            project.permittedActions?.includes('EDIT') ||
            project.permittedActions?.includes('INVITE_USER')

          const onSettingsClick = canSeeSettingsIcon
            ? () =>
                router.push(
                  PageUrls.projectSettings({
                    organisationId,
                    projectId: project.id,
                  }),
                )
            : undefined
          return {
            ...project,
            name: projectName,
            taskBadgeCount:
              userStats?.tasks?.assignedActiveTasksCountByProject?.[project.id],
            observationBadgeCount:
              userStats?.observations?.submittedObservationCountByProject?.[
                project.id
              ],
            onSettingsClick,
            menuItems: [
              {
                title: localizedStrings.overview,
                href: PageUrls.projectOverview({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <DashboardOutlined />,
                dataTest: 'jsOverview',
              },
              {
                title: localizedStrings.analytics,
                href: PageUrls.projectAnalytics({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <BarChartOutlined />,
                dataTest: 'jsAnalytics',
              },
              {
                title: localizedStrings.users,
                href: PageUrls.projectUsers(organisationId, project.id),
                icon: <PeopleAltOutlined />,
                dataTest: 'project-users',
              },
              {
                title: localizedStrings.map,
                href: PageUrls.projectMap({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <PlaceOutlined />,
                dataTest: 'jsMap',
              },
              {
                title: localizedStrings.plans,
                href: PageUrls.projectViewPlans(organisationId, project.id),
                icon: <MapOutlined />,
                dataTest: 'jsPlans',
              },
              {
                title: localizedStrings.permits,
                href: PageUrls.projectPermits(organisationId, project.id),
                icon: <ArticleOutlined />,
                dataTest: 'jsPermits',
                actionTooltipLabel: localizedStrings.createPermit,
                actionHref: PageUrls.newPermitProject({
                  organisationId,
                  projectId: project.id,
                }),
              },
              {
                title: localizedStrings.checklists,
                href: PageUrls.projectChecklists({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <FactCheckOutlined />,
                dataTest: 'jsChecklists',
                actionTooltipLabel: localizedStrings.createChecklist,
                actionHref: PageUrls.newChecklistProject({
                  organisationId,
                  projectId: project.id,
                }),
              },
              {
                title: localizedStrings.inspections,
                href: PageUrls.projectInspections({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <FlagOutlined />,
                dataTest: 'jsInspections',
                actionTooltipLabel: localizedStrings.createInspection,
                actionHref: PageUrls.newInspectionProject({
                  organisationId,
                  projectId: project.id,
                }),
              },
              {
                title: localizedStrings.tasks,
                href: PageUrls.projectTasks({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <AssignmentOutlined />,
                dataTest: 'jsTasks',
                actionTooltipLabel: localizedStrings.createTask,
                actionHref: PageUrls.newTaskProject({
                  organisationId,
                  projectId: project.id,
                }),
              },
              {
                title: localizedStrings.observations,
                href: PageUrls.projectObservations({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <ReportGmailerrorredIcon />,
              },
              {
                title: localizedStrings.registers,
                href: PageUrls.projectRegisters(organisationId, project.id),
                icon: <FolderCopyOutlined />,
                dataTest: 'jsRegisters',
              },
              {
                title: localizedStrings.siteBox,
                href: PageUrls.projectSiteBox({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <Inventory2Outlined />,
                dataTest: 'jsSiteBox',
              },
              {
                title: localizedStrings.procore,
                href: PageUrls.projectProcore({
                  organisationId,
                  projectId: project.id,
                }),
                icon: <CloudOutlined />,
                dataTest: 'jsProcore',
              },
              {
                title: localizedStrings.timeline,
                href: PageUrls.projectTimeline(organisationId, project.id),
                icon: <ViewTimelineOutlined />,
                dataTest: 'jsTimeline',
              },
            ],
          }
        }),
        links:
          navProjects.length > 5
            ? [
                {
                  title: localizedStrings.more,
                  href: PageUrls.projects(organisationId),
                  neverActive: true,
                  icon: <MoreHorizOutlined />,
                  isMoreItem: true,
                },
              ]
            : undefined,
        collapsible: true,
        defaultExpanded: true,
      })
    }

    if (navBusinessUnits.length > 0) {
      let businessUnitsToShow = navBusinessUnits.slice(0, 5)

      if (
        businessUnitId &&
        currentBusinessUnit &&
        !businessUnitsToShow.some((bu) => bu.businessUnitId === businessUnitId)
      ) {
        businessUnitsToShow = [
          {
            businessUnitId: currentBusinessUnit.id,
            businessUnitName: currentBusinessUnit.name,
          },
          ...businessUnitsToShow,
        ]
      }

      if (businessUnitsToShow.length > 0) {
        sections.push({
          title: localizedStrings.businessUnits,
          businessUnits: businessUnitsToShow.map((businessUnit) => ({
            id: businessUnit.businessUnitId,
            name: businessUnit.businessUnitName,
            menuItems: [
              {
                title: localizedStrings.overview,
                href: PageUrls.businessUnitOverview({
                  id: organisationId,
                  businessUnitId: businessUnit.businessUnitId,
                }),
                icon: <HomeOutlined />,
              },
              {
                title: localizedStrings.analytics,
                href: PageUrls.businessUnitAnalytics({
                  organisationId,
                  businessUnitId: businessUnit.businessUnitId,
                }),
                icon: <BarChartOutlined />,
              },
              {
                title: localizedStrings.projects,
                href: PageUrls.businessUnitProjects({
                  id: organisationId,
                  businessUnitId: businessUnit.businessUnitId,
                }),
                icon: <FolderOpenOutlined />,
              },
              {
                title: localizedStrings.permits,
                href: PageUrls.businessUnitPermits({
                  id: organisationId,
                  businessUnitId: businessUnit.businessUnitId,
                }),
                icon: <ArticleOutlined />,
              },
              {
                title: localizedStrings.checklists,
                href: PageUrls.businessUnitChecklists({
                  id: organisationId,
                  businessUnitId: businessUnit.businessUnitId,
                }),
                icon: <FactCheckOutlined />,
              },
            ],
          })),
          links:
            navBusinessUnits.length > 5
              ? [
                  {
                    title: localizedStrings.more,
                    href: PageUrls.businessUnits(organisationId),
                    neverActive: true,
                    icon: <MoreHorizOutlined />,
                    isMoreItem: true,
                  },
                ]
              : undefined,
          collapsible: true,
          defaultExpanded: true,
        })
      }
    }

    return { organisationLinks, moreLinks, sections, backButton: undefined }
  }, [
    flags,
    organisation,
    organisationId,
    inSupportContext,
    isCompetencyBlocked,
    userStats,
    projectId,
    businessUnitId,
    handleNotificationClick,
    notificationsDrawerOpen,
    unseenNotificationsCount,
    canSeeNewButton,
    isSupportRole,
    profile,
    currentProject,
    currentBusinessUnit,
  ])

  const organisationSettingsSidebarData = useDeepCompareMemo(() => {
    if (!inOrganisationSettingsContext) return null

    const generalSettingsLinks: SidebarLinkOption[] = [
      {
        title: localizedStrings.general,
        href: PageUrls.organisationGeneralSettings({ organisationId }),
        icon: <SettingsOutlined />,
      },
    ]

    if (
      flags?.web_mfa_settings &&
      organisation?.canSeeOrganisationAuthenticationConfig()
    ) {
      generalSettingsLinks.push({
        title: localizedStrings.authentication,
        href: PageUrls.organisationAuthenticationSettings({ organisationId }),
        icon: <LockOutlined />,
      })
    }

    if (organisation?.canSeeOrganisationSSOConfig()) {
      generalSettingsLinks.push({
        title: localizedStrings.ssoConfiguration,
        href: PageUrls.organisationSSOSettings({ organisationId }),
        icon: <VpnKeyOutlined />,
      })
    }

    if (organisation?.canSeeAuthorisationGroups()) {
      generalSettingsLinks.push({
        title: localizedStrings.authorisationGroups,
        href: PageUrls.organisationAuthorisationGroupList({ organisationId }),
        icon: <PeopleAltOutlined />,
      })
    }

    if (flags.web_company_settings) {
      generalSettingsLinks.push({
        title: localizedStrings.companies,
        href: PageUrls.organisationCompaniesSettings({ organisationId }),
        icon: <BusinessOutlined />,
        dataTest: 'organisation-companies',
      })
    }

    generalSettingsLinks.push({
      title: localizedStrings.tags,
      href: PageUrls.organisationProcessTags({ organisationId }),
      icon: <LocalOfferOutlined />,
    })

    if (organisation?.canSeeLinkedOrganizations(profile?.role)) {
      generalSettingsLinks.push({
        title: localizedStrings.linkedOrganisations,
        href: PageUrls.linkedOrganisations(organisationId),
        icon: <LinkOutlined />,
        dataTest: 'jsLinkedOrganisations',
      })
    }

    if (organisation?.canSeeDeveloperSection()) {
      generalSettingsLinks.push({
        title: localizedStrings.developer,
        href: PageUrls.developerSection({ organisationId }),
        icon: <CodeOutlined />,
      })
    }

    if (organisation?.canSetFeatureFlags()) {
      generalSettingsLinks.push({
        title: localizedStrings.featureFlags,
        href: PageUrls.organisationFeatureFlagsSettings({ organisationId }),
        icon: <FlagOutlined />,
      })
    }

    const templateLinks: SidebarLinkOption[] = [
      {
        title: localizedStrings.permits,
        href: PageUrls.organisationPermitTemplates({ organisationId }),
        icon: <ArticleOutlined />,
      },
      {
        title: localizedStrings.checklists,
        href: PageUrls.organisationChecklistTemplates({ organisationId }),
        icon: <FactCheckOutlined />,
      },
      {
        title: localizedStrings.inspections,
        href: PageUrls.organisationInspectionTemplates({
          organisationId,
        }),
        icon: <FlagOutlined />,
      },
      {
        title: localizedStrings.registers,
        href: PageUrls.organisationRegisterTemplates({ organisationId }),
        icon: <FolderCopyOutlined />,
      },
    ]

    templateLinks.push({
      title: localizedStrings.tasks,
      href: PageUrls.organisationTaskTemplates({ organisationId }),
      icon: <AssignmentOutlined />,
    })

    if (flags.web_company_settings) {
      templateLinks.push({
        title: localizedStrings.competencies,
        href: PageUrls.organisationCompetencyTemplates({ organisationId }),
        icon: <SchoolOutlined />,
      })
    }

    if (
      flags.web_site_box_rams_appraisals &&
      organisation?.canViewDocumentEngineTemplates()
    ) {
      templateLinks.push({
        title: localizedStrings.documents,
        href: PageUrls.organisationDocumentEngineTemplates({ organisationId }),
        icon: <InsertDriveFileOutlined />,
      })
    }

    const sections: SidebarSection[] = [
      {
        title: '',
        links: generalSettingsLinks,
        collapsible: false,
        defaultExpanded: true,
      },
      {
        title: localizedStrings.templates,
        links: templateLinks,
        collapsible: false,
        defaultExpanded: true,
      },
    ]

    return {
      organisationLinks: [],
      moreLinks: [],
      sections,
      backButton: {
        label: localizedStrings.backToHome,
        onClick: () => {
          if (projectId) {
            router.push(PageUrls.projectOverview({ organisationId, projectId }))
          } else {
            router.push(PageUrls.organisation(organisationId))
          }
        },
      },
    }
  }, [
    flags,
    organisation,
    organisationId,
    projectId,
    inOrganisationSettingsContext,
    profile,
  ])

  const projectSettingsSidebarData = useDeepCompareMemo(() => {
    if (!inProjectSettingsContext) return null

    const generalSettingsLinks: SidebarLinkOption[] = [
      {
        title: localizedStrings.general,
        href: PageUrls.projectSettingsGeneral({ organisationId, projectId }),
        icon: <SettingsOutlined />,
      },
      {
        title: localizedStrings.authorisationGroups,
        href: PageUrls.projectAuthorisationGroupList({
          organisationId,
          projectId,
        }),
        icon: <PeopleAltOutlined />,
      },
    ]

    generalSettingsLinks.push(
      {
        title: localizedStrings.siteLocations,
        href: PageUrls.projectLocations({ organisationId, projectId }),
        icon: <PlaceOutlined />,
      },
      {
        title: localizedStrings.map,
        href: PageUrls.projectMapSettings({ organisationId, projectId }),
        icon: <MapOutlined />,
      },
    )

    if (currentProject?.isFeatureEnabled('OBSERVATIONS')) {
      generalSettingsLinks.push({
        title: localizedStrings.observations,
        href: PageUrls.projectObservationSettings({
          organisationId,
          projectId,
        }),
        icon: <ReportGmailerrorredIcon />,
      })
    }

    if (flags.procore_integration) {
      generalSettingsLinks.push({
        title: localizedStrings.integrations,
        href: PageUrls.projectIntegrations({ organisationId, projectId }),
        icon: <CloudOutlined />,
      })
    }

    if (currentProject?.canAccessDigests()) {
      generalSettingsLinks.push({
        title: localizedStrings.digests,
        href: PageUrls.projectDigests({ organisationId, projectId }),
        icon: <MarkEmailReadOutlined />,
        dataTest: 'project-digests',
      })
    }

    if (organisation?.canSetFeatureFlags()) {
      generalSettingsLinks.push({
        title: localizedStrings.featureFlags,
        href: PageUrls.projectFeatureFlagsSettings({
          organisationId,
          projectId,
        }),
        icon: <FlagOutlined />,
      })
    }

    const templateLinks: SidebarLinkOption[] = [
      {
        title: localizedStrings.permits,
        href: PageUrls.projectPermitTemplates({ organisationId, projectId }),
        icon: <ArticleOutlined />,
      },
      {
        title: localizedStrings.checklists,
        href: PageUrls.projectChecklistTemplates({ organisationId, projectId }),
        icon: <FactCheckOutlined />,
      },
      {
        title: localizedStrings.inspections,
        href: PageUrls.projectInspectionTemplates({
          organisationId,
          projectId,
        }),
        icon: <FlagOutlined />,
      },
    ]

    templateLinks.push({
      title: localizedStrings.tasks,
      href: PageUrls.projectTaskTemplates({ organisationId, projectId }),
      icon: <AssignmentOutlined />,
    })

    if (flags.web_site_box_rams_appraisals) {
      templateLinks.push({
        title: localizedStrings.documents,
        href: PageUrls.projectDocumentEngineTemplates({
          organisationId,
          projectId,
        }),
        icon: <InsertDriveFileOutlined />,
      })
    }

    const sections: SidebarSection[] = [
      {
        title: '',
        links: generalSettingsLinks,
        collapsible: false,
        defaultExpanded: true,
      },
      {
        title: localizedStrings.templates,
        links: templateLinks,
        collapsible: false,
        defaultExpanded: true,
      },
    ]

    return {
      organisationLinks: [],
      moreLinks: [],
      sections,
      backButton: {
        label: localizedStrings.backToProject,
        onClick: () =>
          router.push(PageUrls.projectOverview({ organisationId, projectId })),
      },
    }
  }, [
    flags,
    organisation,
    organisationId,
    projectId,
    inProjectSettingsContext,
    currentProject,
  ])

  const supportSidebarData = useDeepCompareMemo(() => {
    if (!inSupportContext) return null

    const organisationLinks: SidebarLinkOption[] = [
      {
        title: localizedStrings.organisations,
        href: PageUrls.adminOrganisations,
        icon: <BusinessOutlined />,
      },
      {
        title: localizedStrings.users,
        href: PageUrls.adminUsers,
        icon: <PeopleAltOutlined />,
      },
      {
        title: localizedStrings.permits,
        href: PageUrls.adminPermits,
        icon: <ArticleOutlined />,
      },
      {
        title: localizedStrings.analytics,
        href: PageUrls.adminAnalytics,
        icon: <BarChartOutlined />,
      },
      {
        title: localizedStrings.Labelling,
        href: PageUrls.supportLabelling,
        icon: <Label />,
      },
      {
        title: localizedStrings.MapboxWsmTest,
        href: PageUrls.supportMapboxWsm,
        icon: <MapOutlined />,
      },
      {
        title: localizedStrings.KAZPOC,
        href: PageUrls.supportKmzPoc,
        icon: <MapOutlined />,
      },
    ]

    if (profile?.isSuperUser()) {
      organisationLinks.push({
        title: localizedStrings.Kafka,
        href: PageUrls.supportKafka,
        icon: <CloudQueueOutlined />,
      })
    }

    organisationLinks.push({
      title: localizedStrings.accessToken,
      href: PageUrls.adminAccessToken,
      icon: <KeyOutlined />,
    })

    return {
      organisationLinks,
      moreLinks: [],
      sections: [],
      backButton: undefined,
    }
  }, [inSupportContext, profile])

  const getSidebarData = () => {
    if (inProjectSettingsContext) return projectSettingsSidebarData
    if (inOrganisationSettingsContext) return organisationSettingsSidebarData
    if (inSupportContext) return supportSidebarData
    return organisationSidebarData
  }

  const sidebarData = getSidebarData()

  if (!sidebarData) return null

  const showHeader =
    (organisationId || inSupportContext) &&
    !inOrganisationSettingsContext &&
    !inProjectSettingsContext

  const header = showHeader ? (
    <OrganisationMenu organisationId={organisationId} collapse={false} />
  ) : null

  const showNewButton =
    (organisationId || projectId) &&
    !inSupportContext &&
    !inOrganisationSettingsContext &&
    !inProjectSettingsContext &&
    canSeeNewButton

  const newButton = showNewButton ? (
    <SidebarNewButton organisationId={organisationId} projectId={projectId} />
  ) : null

  const showUnimpersonate =
    profile?.impersonated &&
    !inProjectSettingsContext &&
    !inOrganisationSettingsContext

  const actionButtons = showUnimpersonate ? (
    <SidebarUnimpersonateButton />
  ) : null

  const singleProjectId =
    userStats?.projects?.length === 1 ? userStats.projects[0].id : undefined

  useEffect(() => {
    const closeNotificationsDrawer = () => setNotificationsDrawerOpen(false)
    router.events.on('routeChangeStart', closeNotificationsDrawer)
    return () => router.events.off('routeChangeStart', closeNotificationsDrawer)
  }, [router.events])

  return (
    <>
      <SideBar
        profile={profile}
        header={header}
        actionButtons={actionButtons}
        newButton={newButton}
        sections={sidebarData.sections}
        organisationLinks={sidebarData.organisationLinks || []}
        moreLinks={sidebarData.moreLinks || []}
        footerLinks={footerLinks}
        backButton={sidebarData.backButton}
        currentProjectId={projectId}
        currentBusinessUnitId={businessUnitId}
        singleProjectId={singleProjectId}
        ref={ref}
        onCollapsedChange={onCollapsedChange}
        autoCollapseBelow={autoCollapseBelow}
      />
      <Drawer
        open={notificationsDrawerOpen}
        ModalProps={{
          keepMounted: false,
          container: contentRef.current,
        }}
        SlideProps={{
          container: contentRef.current,
        }}
        onClose={() => setNotificationsDrawerOpen(false)}
        sx={{
          width: 'auto',
          position: 'absolute',
          '& .MuiBackdrop-root': {
            position: 'absolute',
          },
          '& .MuiDrawer-paper': {
            position: 'absolute',
          },
        }}
        anchor='left'
      >
        <NotificationsList
          pageSize={25}
          onClose={() => setNotificationsDrawerOpen(false)}
        />
      </Drawer>
    </>
  )
})

NavigationAwareSidebar.displayName = 'NavigationAwareSidebar'

export { NavigationAwareSidebar }
